'use client'

import { EditableImage } from '@/components/content-edit/editable-image'
import { Icon } from '@/components/ui/icon'
import type { ImageSlotEntry } from '@/lib/content-edit/sources'

interface ImagePlaceholderProps {
  /** REQUIRED description of the image that needs to be produced for this
   *  slot, e.g. "Image needed: swatch macro photo of 3M 2080 Gloss Hot Rod
   *  Red with an on-vehicle shot". Doubles as the accessible name. */
  alt: string
  className?: string
  rounded?: string
  iconSize?: 'sm' | 'md' | 'lg' | 'xl'
  /** Stable image-slot key; enables click-to-replace in staging edit mode. */
  slot?: string
  /**
   * Manifest entry for `slot`. This is a client component, so it cannot read
   * the manifest itself: a server parent must pass the entry down. No call
   * site is wired in this project yet; the prop is the capability.
   */
  slotImage?: ImageSlotEntry | null
}

/**
 * Honest image placeholder: an icon tile for every image slot whose real
 * asset does not exist yet. The alt text states exactly what photograph is
 * needed, so the required content is self-documenting in the UI and in
 * accessibility tooling (house rule 2026-07-22: no colour fills pretending
 * to be photography).
 */
export function ImagePlaceholder({
  alt,
  className = '',
  rounded = 'rounded-lg',
  iconSize = 'lg',
  slot,
  slotImage = null
}: ImagePlaceholderProps) {
  const rendered = slotImage ? (
    // Plain img, not next/image: the media CDN host is not in the next/image
    // remote allowlist, and adding it is out of scope for content edit mode.
    <img
      src={slotImage.url}
      alt={slotImage.alt}
      width={slotImage.width}
      height={slotImage.height}
      className={`${rounded} h-full w-full object-cover ${className}`}
    />
  ) : (
    <div
      role='img'
      aria-label={alt}
      title={alt}
      className={`${rounded} flex items-center justify-center bg-gray-200 ${className}`}
    >
      <Icon icon='image' size={iconSize} className='text-gray-500' />
    </div>
  )

  if (!slot) return rendered

  return (
    <EditableImage slot={slot} current={slotImage}>
      {rendered}
    </EditableImage>
  )
}
